import * as React from 'react';
import noop from 'lodash/noop';

import {showDialog} from '../state';
import type {DialogProps} from '../types';
import {Dialog, SubmitButton} from './shared';
import type {ButtonType, IconType, Size} from './shared';

type AlertConfig = {
  content: React.ReactNode;
  iconType?: IconType;
  onSubmit?: () => void;
  size?: Size;
  submit?: ButtonType;
  // The React.Element type acts as an escape hatch
  // for Logic to implement breadcrumbs.
  title: string | React.ReactElement;
};

type AlertDialogProps = AlertConfig & {
  onSubmit: DialogProps['onSubmit'];
  style?: React.CSSProperties;
};

export const AlertDialog = React.memo<AlertDialogProps>(function Alert({
  content,
  iconType,
  onSubmit,
  size,
  submit = {label: 'Submit', intent: 'primary', disabled: false},
  title,
  style,
}) {
  return (
    <Dialog
      content={content}
      iconType={iconType}
      onClose={onSubmit}
      size={size}
      title={title}
      buttons={
        <SubmitButton
          danger={submit.intent === 'danger'}
          disabled={submit.disabled}
          data-automation-id={submit.label}
          onClick={onSubmit}
          primary={submit.intent === 'primary'}
        >
          {submit.label}
        </SubmitButton>
      }
      style={style}
    />
  );
});

export const alert = ({onSubmit = noop, ...staticProps}: AlertConfig) =>
  showDialog(function AlertWrapper({onSubmit: internalSubmit}) {
    const handleSubmit = React.useCallback(() => {
      onSubmit();
      internalSubmit();
    }, [internalSubmit]);

    return <AlertDialog {...staticProps} onSubmit={handleSubmit} />;
  });
